/**
 * Reusable form modal — header + body + footer. Esc to close. Click backdrop
 * to dismiss. Submission is owned by the consumer (so it can run a mutation
 * and decide when to close).
 */

import { useEffect, useRef } from 'react';
import styles from './FormModal.module.css';

export interface FormModalProps {
  open: boolean;
  onClose: () => void;
  /** Header label, e.g. "New project". */
  title: string;
  /** Optional subtitle / explanatory line under the title. */
  subtitle?: string | undefined;
  /** Form body (fields). */
  children: React.ReactNode;
  /** Primary action label. Defaults to "Save". */
  submitLabel?: string;
  /** Disable the submit button. */
  submitDisabled?: boolean | undefined;
  /** Called when the form is submitted. */
  onSubmit: () => void;
  /** Optional error banner shown above the footer. */
  error?: string | null | undefined;
  /** Tertiary destructive action (e.g. Archive on edit). Renders bottom-left. */
  destructiveAction?: { label: string; onClick: () => void } | undefined;
}

export function FormModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  submitLabel = 'Save',
  submitDisabled,
  onSubmit,
  error,
  destructiveAction,
}: FormModalProps) {
  const formRef = useRef<HTMLFormElement | null>(null);

  // Focus the first input on open.
  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => {
      const first = formRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input,textarea,select',
      );
      first?.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open]);

  if (!open) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.head}>
          <div>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
        </div>

        <form
          ref={formRef}
          className={styles.body}
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitDisabled) onSubmit();
          }}
        >
          {children}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.footer}>
            <div className={styles.footerLeft}>
              {destructiveAction && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDanger}`}
                  onClick={destructiveAction.onClick}
                >
                  {destructiveAction.label}
                </button>
              )}
            </div>
            <div className={styles.footerRight}>
              <button type="button" className={styles.btn} onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={submitDisabled}
              >
                {submitLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
